import { Chip, Tooltip, Typography } from '@material-tailwind/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Avatar from '../components/Avatar'
import { useFeatureAuth } from '../hooks/authorization'
import { useMountEffect, useScreenBreakpoint } from '../hooks/toolkit'
import { FEATURES } from '../utils/constants/features.constants'
import { getExperienceSpotsCount } from '../utils/helpers/supabase.helpers'
import DownloadExpButton from './DownloadExpButton'
import { ExperienceOwner } from '../types/supabase'

interface ExperienceCardProps {
  isActive: boolean
  experienceId: string
  name: string
  owner?: ExperienceOwner
  isLibrary?: boolean
  isSelected: boolean
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({
  isActive,
  experienceId,
  name,
  owner,
  isLibrary,
  isSelected,
}) => {
  const canDownloadExp = useFeatureAuth(FEATURES.DOWNLOAD_EXPERIENCE)
  const { t } = useTranslation()

  const [spotsCount, setSpotsCount] = useState<number | null>(null)

  const expOwner = owner ? `${owner.name} ${owner.surname}` : null

  const getCount = async () => {
    const { count, error } = await getExperienceSpotsCount({ experienceId })

    if (error) {
      return
    }

    setSpotsCount(count)
  }

  useMountEffect(() => {
    getCount()
  })

  const screen = useScreenBreakpoint()

  const smallScreen = screen === 'sm' || screen === 'md'

  return (
    <Link
      to={`/experience/${experienceId}`}
      className={`
        flex items-center space-x-4 p-2 rounded
        px-4 dark:bg-primary-400
        ${isSelected ? 'bg-white-yellow' : 'bg-white'}
        ${isActive ? 'opacity-100 hover:bg-base-gray-500 dark:hover:bg-primary-500 cursor-pointer' : 'opacity-50 pointer-events-none'}
      `}
      data-testclass="experience-explore-card"
      data-experience-name={name}
    >
      <div className="w-full flex gap-4 items-center justify-between dark:text-white">
        <div className="w-full flex gap-2 items-center">
          {isSelected && <div className="w-4 h-4 rounded-full bg-accent shadow-lg" />}
          <div className="hidden md:block">
            <Avatar size={smallScreen ? 'md' : 'lg'} name={name} surname={name.split(' ')[1]} />
          </div>
          <div className="flex-shrink-0">
            <Typography
              variant={smallScreen ? 'h6' : 'h5'}
              className="font-bold"
              data-testclass="experience-explore-card-name"
            >
              {name}
            </Typography>
            <Typography variant="small">
              {t('experienceCard.by')} <strong>{expOwner || 'MezzoForte'}</strong> |{' '}
              <strong>{spotsCount}</strong> {t('experienceCard.agami')}
            </Typography>
            {isLibrary && (
              <Tooltip content={t('experienceCard.yourLibraryTooltip')} placement="right">
                <Chip
                  color="yellow"
                  value={t('experienceCard.yourLibrary')}
                  size="sm"
                  className="inline-block"
                />
              </Tooltip>
            )}
          </div>
        </div>

        {isActive && canDownloadExp && <DownloadExpButton experienceId={experienceId} />}
      </div>
    </Link>
  )
}

export default ExperienceCard
